import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, withModifiers as _withModifiers, withKeys as _withKeys, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-6655665e"),n=n(),_popScopeId(),n)
const _hoisted_1 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", {
  class: "head-background",
  "data-test": "head-background-img"
}, null, -1))
const _hoisted_2 = { class: "head-dot-container" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "head-crosshair-horizontal" }, null, -1))
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "head-crosshair-vertical" }, null, -1))
const _hoisted_5 = { key: 0 }
const _hoisted_6 = ["onClick", "onKeydown", "data-test", "leader-type"]

import { computed, onMounted, ref, watch } from "vue";
import { TimelineMax, Back } from "gsap";
import { Tooltip } from "uiv";
import cms from "../../services/cms/cms";
import cmsContentMap from "../../services/cms/cms-content-map";
import { CmsTeamEffectivenessReview } from "../../services/cms/cms-type-definitions";
import { HeadValueTypeApiModel, LeaderTypesApiModel } from "../../api-models";
import { Kind, FillOptions } from "./head-models";

const axisScale = 18;
const spotScale = 0.3;


export default /*#__PURE__*/_defineComponent({
  __name: 'gyre-head',
  props: {
    animateEntrance: { type: Boolean, default: false },
    kind: {},
    values: { default: null },
    valueType: {},
    showTooltips: { type: Boolean, default: true },
    fillOverride: { default: null },
    containerForTooltips: { default: "body" },
    whiteBackground: { type: Boolean, default: true }
  },
  emits: ["leaderTypeSelection"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

// template refs
const thisHead = ref<HTMLElement | null>(null);

function getDotsByAscendingSize(dots: HTMLElement[]): HTMLElement[] {
  return dots.sort((a, b) => {
    const aWidth = parseFloat(a.style.width);
    const bWidth = parseFloat(b.style.width);
    if (!aWidth) {
      return -1;
    }
    if (!bWidth) {
      return 1;
    }
    if (aWidth < bWidth) {
      return -1;
    }
    if (aWidth > bWidth) {
      return 1;
    }

    return 0;
  });
}

const teamEffectivenessReview = ref<CmsTeamEffectivenessReview | null>(null);

const tooltipClass = computed(() => {
  return props.containerForTooltips !== "body" ? "head-tooltips-fixed" : "head-tooltips";
});

const isTeamEffectivenessHead = computed(() => {
  return (
    props.kind === "TeamEffectivenessAggregateDiamond" ||
    props.kind === "TeamEffectivenessIndividualDiamond"
  );
});

function getTeamEffectivenessDoc() {
  if (isTeamEffectivenessHead.value) {
    cms
      .getByUid("cms-team-effectiveness-review", cmsContentMap.teamEffectiveness.questionnaireUid)
      .then((doc) => {
        teamEffectivenessReview.value = doc;
      });
  }
}

getTeamEffectivenessDoc();

const leaderTypes: LeaderTypesApiModel[] = [
  "Professional",
  "Entrepreneurial",
  "Executional",
  "Practical",
  "Strategist",
  "Servant",
  "Transformational",
  "Charismatic",
  "Innovator",
];

function emitLeaderTypeSelect(leaderType: string): void {
  emit("leaderTypeSelection", leaderType);
}

function getStyles(leaderType: string, kind: Kind): string[] {
  return [
    `dot-${leaderType.toLowerCase()} head-kind-${kind} ${
      props.fillOverride ? `fill-override-${props.fillOverride}` : ""
    }`,
  ];
}

function getSizePercent(value: number) {
  return (props.valueType === "DecimalValue" ? Math.floor(value * 10) : value) * spotScale;
}

function style(leaderType: string): Record<string, string | number> {
  if (!props.values) {
    return {
      display: "none",
    };
  }

  const positions: Record<LeaderTypesApiModel, { x: number; y: number }> = {
    Innovator: { x: 0, y: -2 },
    Entrepreneurial: { x: -1, y: -1 },
    Executional: { x: -2, y: -0 },
    Practical: { x: -1, y: 1 },
    Strategist: { x: 0, y: 2 },
    Servant: { x: 1, y: 1 },
    Transformational: { x: 2, y: 0 },
    Charismatic: { x: 1, y: -1 },
    Professional: { x: 0, y: 0 },
  };

  const x = positions[leaderType].x * axisScale;
  const y = positions[leaderType].y * axisScale;

  const sizePercent = getSizePercent(props.values[leaderType]);
  // const widthAndHeight = props.willAnimate ? 0 : `${sizePercent}%`;
  const widthAndHeight = `${sizePercent}%`;

  return {
    left: `${x - sizePercent * 0.5 + 50}%`,
    top: `${y - sizePercent * 0.5 + 50}%`,
    width: widthAndHeight,
    height: widthAndHeight,
    zIndex: 2,
    position: "absolute",
    opacity: 0.9,
  };
}

function getValuesByAscendingSize() {
  if (!props.values) return null;
  // Step 1: Convert the object into an array of [key, value] pairs
  const entries = Object.entries(props.values);

  // Step 2: Sort the array based on the values
  const sortedEntries = entries.sort((a, b) => a[1] - b[1]);

  return sortedEntries;
}

function getDot(leaderType: string) {
  return thisHead.value?.querySelector(`.head-dot[leader-type="${leaderType}"]`) as HTMLElement;
}

function runEntranceAnimation(): void {
  const values = getValuesByAscendingSize();
  if (!values) return;
  const timeline = new TimelineMax();
  for (let i = 0; i < values.length; i++) {
    timeline.from(
      getDot(values[i][0]),
      0.3,
      { height: 0, width: 0, delay: i * 0.2, ease: Back.easeOut },
      0,
    );
    getDot(values[i][0]).style.zIndex = String(2 + i);
  }
}

function onAnimateChange() {
  if (props.animateEntrance) {
    setTimeout(() => {
      runEntranceAnimation();
    });
  }
}

watch(() => props.animateEntrance, onAnimateChange);

function getAllStylesByLeaderType(): Record<string, Record<string, string | number>> {
  return leaderTypes
    .map((leaderType) => {
      return { leaderType, styles: style(leaderType) };
    })
    .reduce((obj, value) => {
      obj[value.leaderType] = value.styles;
      return obj;
    }, {});
}

function tooltipText(leaderType: string): string {
  if (!props.showTooltips) {
    return ""; // Empty tooltips are not displayed at all;
  }

  if (!props.values) {
    return "";
  }

  if (isTeamEffectivenessHead.value) {
    const text = cms.asText(
      teamEffectivenessReview.value?.data.body.find(
        (slice) => slice.primary.question_id === leaderType,
      )?.primary.question_title,
    );

    return `${text}: ${props.values[leaderType].toFixed(1)}`;
  }

  return `${leaderType}: ${props.values[leaderType]}% fit`;
}

// this is so cms-session can trigger runEntranceAnimation during a session
function playEntranceAnimationForSessions(): void {
  runEntranceAnimation();
}

onMounted(onAnimateChange);

__expose({
  playEntranceAnimationForSessions,
  getAllStylesByLeaderType,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "head",
    ref_key: "thisHead",
    ref: thisHead
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["head-outline", {
        'head-outline-solo': _ctx.kind === 'solo' || _ctx.kind === 'teamsolo' || _ctx.kind === 'orgsolo',
        'head-outline-viewpoints': _ctx.kind === 'viewpoints',
        'head-outline-team-effectiveness':
          _ctx.kind === 'TeamEffectivenessAggregateDiamond' ||
          _ctx.kind === 'TeamEffectivenessIndividualDiamond',
        'head-outline-solo-pair': (_ctx.kind === 'solo' || _ctx.kind === 'teamsolo') && _ctx.whiteBackground,
        'head-outline-viewpoints-pair':
          (_ctx.kind === 'viewpoints' || _ctx.kind === 'teamviewpoints' || _ctx.kind === 'orgviewpoints') &&
          _ctx.whiteBackground,
      }])
    }, [
      _hoisted_1,
      _createElementVNode("div", _hoisted_2, [
        _hoisted_3,
        _hoisted_4,
        (!!_ctx.values && !!_ctx.containerForTooltips)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(leaderTypes, (leaderType, index) => {
                return _createVNode(_unref(Tooltip), {
                  tag: "div",
                  customClass: tooltipClass.value,
                  key: index,
                  text: tooltipText(leaderType),
                  transition: 0,
                  enterable: false,
                  "append-to": _ctx.containerForTooltips
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", {
                      style: _normalizeStyle(style(leaderType)),
                      class: _normalizeClass(["head-dot", getStyles(leaderType, _ctx.kind)]),
                      onClick: ($event: any) => (emitLeaderTypeSelect(leaderType)),
                      onKeydown: _withKeys(_withModifiers(($event: any) => (emitLeaderTypeSelect(leaderType)), ["prevent"]), ["space"]),
                      "data-test": `head-dot-${leaderType}`,
                      "leader-type": leaderType
                    }, null, 46, _hoisted_6)
                  ]),
                  _: 2
                }, 1032, ["customClass", "text", "append-to"])
              }), 64))
            ]))
          : _createCommentVNode("", true)
      ])
    ], 2)
  ], 512))
}
}

})